import { useEffect, useState } from 'react'
import { Box, LinearProgress, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useCropData } from '~/crop/crop-list/use-crop-data'
import { useMeasurementUnits } from '~/common/use-measurement-units'
import { PlotArea } from './plot-area'
import { useValueFlash } from './use-value-flash'
import { useHarvestSummary } from './use-harvest-summary'

const flashSx = (isFlashing) => (theme) => ({
  px: 0.5,
  borderRadius: 1,
  transition: 'background-color 0.1s ease-in',
  ...(isFlashing && {
    animation: 'valueFlash 1.2s ease-out forwards',
    '@keyframes valueFlash': {
      '0%': { backgroundColor: alpha(theme.palette.success.main, 0.3) },
      '100%': { backgroundColor: 'transparent' },
    },
  }),
})

const IS_COL_BREAKPOINT = 'lg'

export const NumericTotals = ({ flatList = false }) => {
  const { crops } = useCropData()
  const { formatMass } = useMeasurementUnits()
  const { summary } = useHarvestSummary(crops)

  const [{ totalKg, cropCount, plantCount }, setTotals] = useState({ totalKg: 0, cropCount: 0, plantCount: 0 })

  useEffect(() => {
    function computeTotals() {
      if (!crops || !crops.length) return
      const harvested = crops.filter(c => c.yield != null)
      setTotals({
        totalKg: harvested.reduce((sum, c) => sum + (c.yield || 0), 0),
        cropCount: crops.length,
        plantCount: summary.reduce((sum, r) => sum + (r.plantCount || 0), 0),
      })
    }
    computeTotals()
  }, [crops, summary])

  const yieldFlash = useValueFlash(totalKg)
  const cropFlash = useValueFlash(cropCount)
  const plantFlash = useValueFlash(plantCount)

  const formatted = formatMass(totalKg)
  const spaceIdx = formatted.lastIndexOf(' ')
  const displayValue = formatted.slice(0, spaceIdx)
  const displayUnit = formatted.slice(spaceIdx + 1)

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: { xs: 'row', [IS_COL_BREAKPOINT]: 'column' },
        alignItems: 'center',
        justifyContent: 'space-around',
        gap: { xs: 1.5, [IS_COL_BREAKPOINT]: 0 },
        px: { xs: 0, [IS_COL_BREAKPOINT]: 0.5 },
        py: { xs: 0, [IS_COL_BREAKPOINT]: 2 },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.05em' }}>
          YIELD
        </Typography>
        <Stack direction='row' spacing={0.5} alignItems='flex-end' sx={[{ mt: -0.75 }, flashSx(yieldFlash)]}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
            {displayValue}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {displayUnit}
          </Typography>
        </Stack>
      </Box>

      {/* Crop & plant counts */}
      {flatList ? (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.05em' }}>
              CROPS
            </Typography>
            <Typography variant='h5' sx={[{ fontWeight: 700, color: 'warning.main', mt: -0.75 }, flashSx(cropFlash)]}>
              {cropCount}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.05em' }}>
              PLANTS
            </Typography>
            <Typography variant='h5' sx={[{ fontWeight: 700, color: 'warning.main', mt: -0.75 }, flashSx(plantFlash)]}>
              {plantCount}
            </Typography>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', [IS_COL_BREAKPOINT]: 'row' },
            gap: { xs: 0.5, [IS_COL_BREAKPOINT]: 1.5 },
            mt: { xs: 0, [IS_COL_BREAKPOINT]: 0.75 },
            mb: { xs: 0, [IS_COL_BREAKPOINT]: 0.25 },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.05em' }}>
              CROPS
            </Typography>
            <Typography variant='h5' sx={[{ fontWeight: 700, color: 'warning.main', mt: -0.75 }, flashSx(cropFlash)]}>
              {cropCount}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.05em' }}>
              PLANTS
            </Typography>
            <Typography variant='h5' sx={[{ fontWeight: 700, color: 'warning.main', mt: -0.75 }, flashSx(plantFlash)]}>
              {plantCount}
            </Typography>
          </Box>
        </Box>
      )}

      <PlotArea />
    </Box>
  )
}
